import { Badge, Box, Button, Flex, Heading, Image, Stack, Table, Text } from '@chakra-ui/react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { productRowSchema, regions } from '@planner/config';
import { handingImageIds, type ProductDataFile } from '@planner/shared';
import { useAuth } from '../hooks/useAuth.js';
import { getProducts } from '../lib/api.js';
import { cdnEnabled, cdnImageUrl } from '../lib/cdnImage.js';
import { formatCurrency } from '../lib/format.js';

/**
 * Raw `PS-SECBAR-DOORS_StockItems` row as the sync stores it: padded ids and
 * numbers as strings, `metaData` an embedded JSON string.
 */
type RawRow = Record<string, unknown>;

interface MetaItem {
  order?: number;
  description?: string;
}

/** Loose view of `metaData` for display; the schema check is separate. */
interface DoorMetaData {
  itemDetails?: { title?: string; heroDescription?: string };
  features?: { imageCdnId?: string; items?: MetaItem[] };
  whatsIncluded?: { items?: MetaItem[] };
  colorSelection?: { imageCdnId?: string };
}

function text(row: RawRow, key: string): string {
  const value = row[key];
  return value === undefined || value === null ? '' : String(value).trim();
}

function parseMetaData(value: unknown): DoorMetaData | undefined {
  if (typeof value !== 'string') {
    return value && typeof value === 'object' ? (value as DoorMetaData) : undefined;
  }
  try {
    return JSON.parse(value) as DoorMetaData;
  } catch {
    return undefined;
  }
}

function orderedItems(items: MetaItem[] | undefined): string[] {
  return [...(items ?? [])]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((item) => item.description ?? '')
    .filter((description) => description.length > 0);
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box bg="white" p={5} borderRadius="lg" boxShadow="sm">
      <Heading size="md" mb={3}>
        {title}
      </Heading>
      {children}
    </Box>
  );
}

function SpecTable({ rows }: { rows: [string, string][] }) {
  return (
    <Table.Root size="sm" data-name="product-spec">
      <Table.Body>
        {rows.map(([label, value]) => (
          <Table.Row key={label}>
            <Table.Cell fontWeight="medium" color="fg.muted" width="40%">
              {label}
            </Table.Cell>
            <Table.Cell>{value || '—'}</Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  );
}

/**
 * One Cloudinary render. A failed load shows a placeholder carrying the public
 * id, so a missing or misnamed upload is obvious at a glance.
 */
function RenderImage({ imageId, label }: { imageId: string; label: string }) {
  const [failed, setFailed] = useState(false);
  return (
    <Stack gap={1} minW="200px" maxW="260px" data-name="product-image" data-image-id={imageId}>
      <Text fontSize="sm" fontWeight="medium">
        {label}
      </Text>
      {failed ? (
        <Flex
          align="center"
          justify="center"
          h="200px"
          borderWidth="1px"
          borderStyle="dashed"
          borderColor="orange.400"
          borderRadius="md"
          bg="orange.50"
          p={3}
          textAlign="center"
          data-name="product-image-missing"
        >
          <Text fontSize="sm" color="orange.700">
            Image not found
          </Text>
        </Flex>
      ) : (
        <Image
          src={cdnImageUrl(imageId, { width: 520 })}
          alt={label}
          h="200px"
          objectFit="contain"
          bg="gray.50"
          borderRadius="md"
          onError={() => setFailed(true)}
        />
      )}
      <Text fontSize="xs" color="fg.muted" fontFamily="mono" wordBreak="break-all">
        {imageId}
      </Text>
    </Stack>
  );
}

export function ProductDetail() {
  const { region = '', inventoryID = '' } = useParams();
  const { getToken } = useAuth();
  const [data, setData] = useState<ProductDataFile>();
  const [error, setError] = useState<string>();
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    setError(undefined);
    setData(undefined);
    getToken()
      .then((token) => getProducts(token, region, 'products'))
      .then(setData)
      .catch((cause: unknown) => setError((cause as Error).message));
  }, [getToken, region]);

  const backLink = (
    <RouterLink to="/products">
      <Button size="sm" variant="outline" data-name="back-to-products">
        Back to products
      </Button>
    </RouterLink>
  );

  if (error) {
    return (
      <Stack gap={4}>
        <Text color="red.500" data-name="product-error">
          {error}
        </Text>
        {backLink}
      </Stack>
    );
  }

  if (!data) {
    return <Text color="fg.muted">Loading item {inventoryID}…</Text>;
  }

  const row = (data.rows as RawRow[]).find(
    (candidate) => text(candidate, 'inventoryID') === inventoryID,
  );

  if (!row) {
    return (
      <Stack gap={4}>
        <Text color="red.500" data-name="product-error">
          Item {inventoryID} is not in the synced {region.toUpperCase()} catalogue.
        </Text>
        {backLink}
      </Stack>
    );
  }

  const validation = productRowSchema.safeParse(row);
  const meta = parseMetaData(row.metaData);
  const currency = regions.find((candidate) => candidate.code === region)?.currency;
  const price = Number(text(row, 'retailPrice'));
  const status = text(row, 'itemStatus');
  const colourImageId = meta?.colorSelection?.imageCdnId ?? '';
  const baseImageId = meta?.features?.imageCdnId ?? '';
  const handing = colourImageId ? handingImageIds(colourImageId) : undefined;

  return (
    <Stack gap={6} data-name="product-detail">
      <Flex align="center" gap={4} wrap="wrap">
        <Stack gap={1}>
          <Heading size="lg">{text(row, 'description') || inventoryID}</Heading>
          <Flex gap={3} align="center" wrap="wrap">
            <Text fontFamily="mono" data-name="product-inventory-id">
              {inventoryID}
            </Text>
            <Badge colorPalette={status === 'Active' ? 'green' : 'red'} data-name="product-status">
              {status || 'Unknown'}
            </Badge>
            <Text fontSize="sm" color="fg.muted">
              {region.toUpperCase()} · synced {new Date(data.syncedAt).toLocaleString()}
            </Text>
          </Flex>
        </Stack>
        <Box ml="auto">{backLink}</Box>
      </Flex>

      {!validation.success && (
        <Text color="orange.600" fontSize="sm" data-name="product-validation-warning">
          This row does not pass the products feed schema:{' '}
          {validation.error.issues
            .map((issue) => `${issue.path.join('.')} ${issue.message}`)
            .join('; ')}
        </Text>
      )}

      <Section title="Specification">
        <SpecTable
          rows={[
            ['Door type', text(row, 'doorType')],
            ['Frame colour', text(row, 'frameColour')],
            ['Width band', `${text(row, 'minDoorWidth')}–${text(row, 'maxDoorWidth')} mm`],
            ['Height band', `${text(row, 'minDoorHeight')}–${text(row, 'maxDoorHeight')} mm`],
            [
              'Retail price (inc GST)',
              Number.isFinite(price) && currency
                ? formatCurrency(price, currency)
                : text(row, 'retailPrice'),
            ],
            ['Bunnings item number', text(row, 'bunningsItemNumber')],
            ['Bunnings barcode', text(row, 'bunningsPrimaryBarcode')],
          ]}
        />
      </Section>

      <Section title="Renders">
        {!cdnEnabled() && (
          <Text fontSize="sm" color="orange.600" mb={3} data-name="cdn-disabled-notice">
            VITE_CLOUDINARY_CLOUD_NAME is not set for this build, so renders cannot load; the public
            ids below are still the ones the planner will request.
          </Text>
        )}
        {colourImageId || baseImageId ? (
          <Flex gap={6} wrap="wrap">
            {colourImageId && <RenderImage imageId={colourImageId} label="Colour render" />}
            {handing && <RenderImage imageId={handing.left} label="Left hand" />}
            {handing && <RenderImage imageId={handing.right} label="Right hand" />}
            {baseImageId && <RenderImage imageId={baseImageId} label="Base image" />}
          </Flex>
        ) : (
          <Text color="fg.muted" data-name="product-no-images">
            No image ids in metaData.
          </Text>
        )}
      </Section>

      <Section title="Item details (metaData)">
        {meta ? (
          <Stack gap={4} data-name="product-metadata">
            <Box>
              <Text fontWeight="medium" data-name="product-title">
                {meta.itemDetails?.title ?? '—'}
              </Text>
              <Text color="fg.muted" data-name="product-hero">
                {meta.itemDetails?.heroDescription ?? '—'}
              </Text>
            </Box>
            <Box>
              <Text fontSize="xs" color="fg.muted" textTransform="uppercase" mb={1}>
                Features
              </Text>
              <Box as="ol" pl={5} data-name="product-features">
                {orderedItems(meta.features?.items).map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </Box>
            </Box>
            <Box>
              <Text fontSize="xs" color="fg.muted" textTransform="uppercase" mb={1}>
                What&apos;s included
              </Text>
              <Box as="ol" pl={5} data-name="product-included">
                {orderedItems(meta.whatsIncluded?.items).map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </Box>
            </Box>
          </Stack>
        ) : (
          <Text color="orange.600" data-name="product-metadata-invalid">
            metaData is missing or is not valid JSON.
          </Text>
        )}
      </Section>

      <Section title="Raw row">
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowRaw((current) => !current)}
          data-name="raw-json-toggle"
        >
          {showRaw ? 'Hide JSON' : 'Show JSON'}
        </Button>
        {showRaw && (
          <Box
            as="pre"
            mt={3}
            p={3}
            bg="gray.50"
            borderRadius="md"
            fontSize="xs"
            overflowX="auto"
            data-name="raw-json"
          >
            {JSON.stringify({ ...row, metaData: meta ?? row.metaData }, null, 2)}
          </Box>
        )}
      </Section>
    </Stack>
  );
}
